/** @format */
import { ChangeDetectionStrategy, Component, Input, ViewEncapsulation } from '@angular/core';
import { OpenUniversityCart, OtmId } from 'common-typescript/types';
import { BehaviorSubject, combineLatest, distinctUntilChanged, EMPTY, Observable, of, ReplaySubject, shareReplay } from 'rxjs';
import { concatMap, map, switchMap, take, tap } from 'rxjs/operators';
import { AppErrorHandler } from 'sis-components/error-handler/app-error-handler';
import { OpenUniversityCartCustomerService } from 'sis-components/service/open-university-cart-customer.service';
import { StudentOpenUniversityProductAccessTokenService } from '../../../common/service/student-open-university-product-access-token.service';
import { OpenUniversityModalService } from '../../common/service/open-university-modal.service';
import { OpenUniversityErrorHelperService } from '../../service/open-university-error-helper.service';

@Component({
    selector: 'app-add-to-cart-button',
    templateUrl: './add-to-cart-button.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddToCartButtonComponent {
    /**
     * Is an addition or a removal in progress?
     * During this {@link addToCart} and {@link removeFromCart} should not be called.
     */
    readonly requestInProgress$: Observable<boolean>;

    /**
     * Is the addition button visible?
     * Emits the first value when the required input values have been provided.
     */
    readonly isAdditionVisible$: Observable<boolean>;

    /**
     * Is the deletion button visible?
     * Emits the first value when the required input values have been provided.
     */
    readonly isDeletionVisible$: Observable<boolean>;

    private readonly _productId$: BehaviorSubject<OtmId | null> = new BehaviorSubject<OtmId | null>(null);
    private readonly _isProductInCart$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
    private readonly _requiresAccessToken$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
    private readonly _accessToken$: Observable<string | null>;
    private readonly _requestInProgress$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private _courseUnitId: OtmId | undefined;

    constructor(
        private openUniversityCartService: OpenUniversityCartCustomerService,
        private openUniversityErrorHelper: OpenUniversityErrorHelperService,
        private openUniversityModalService: OpenUniversityModalService,
        private appErrorHandler: AppErrorHandler,
        studentOpenUniversityProductAccessTokenService: StudentOpenUniversityProductAccessTokenService,
    ) {
        this._accessToken$ = this._productId$.pipe(
            distinctUntilChanged(),
            switchMap((productId: OtmId | null) =>
                productId ? studentOpenUniversityProductAccessTokenService.getAccessTokenForProduct(productId) : of(null),
            ),
            appErrorHandler.defaultErrorHandler(),
            distinctUntilChanged(),
            shareReplay({ bufferSize: 1, refCount: true }),
        );

        this.isAdditionVisible$ = combineLatest([
            this._isProductInCart$.pipe(distinctUntilChanged()),
            this._requiresAccessToken$.pipe(distinctUntilChanged()),
        ]).pipe(
            switchMap(([isProductInCart, requiresAccessToken]: [boolean, boolean]) =>
                isProductInCart
                    ? // already in cart --> hide add button
                      of(false)
                    : requiresAccessToken
                      ? // requires an access token --> show if there's one
                        this._accessToken$.pipe(map((accessToken: string | null) => accessToken !== null))
                      : // does not require an access token --> show
                        of(true),
            ),
            distinctUntilChanged(),
            shareReplay({ bufferSize: 1, refCount: true }),
        );

        this.isDeletionVisible$ = this._isProductInCart$.pipe(distinctUntilChanged());

        this.requestInProgress$ = this._requestInProgress$.asObservable();
    }

    get productId(): OtmId | null {
        return this._productId$.value;
    }

    @Input({ required: true })
    set productId(value: OtmId) {
        this._productId$.next(value);
    }

    @Input({ required: true })
    set isProductInCart(value: boolean) {
        this._isProductInCart$.next(value);
    }

    @Input({ required: true })
    set requiresAccessToken(value: boolean) {
        this._requiresAccessToken$.next(value);
    }

    @Input({ required: true })
    set courseUnitId(value: OtmId) {
        this._courseUnitId = value;
    }

    addToCart(): void {
        this._requestInProgress$.next(true);

        this.validateNoPaymentInProgress()
            .pipe(
                switchMap(() => this.openUniversityCartService.isProductFromCourseUnitInCurrentCart(this._courseUnitId)),
                take(1),
                switchMap(isInCart => (isInCart ? this.replaceProductInCart() : this.addProductToCart())),
                take(1),
                this.appErrorHandler.defaultErrorHandler(),
            )
            .subscribe({
                next: cart => this.openUniversityModalService.openProductAddedToCartModal(cart, this.productId, this._courseUnitId),
            })
            .add(() => this._requestInProgress$.next(false));
    }

    removeFromCart(): void {
        this._requestInProgress$.next(true);

        this.validateNoPaymentInProgress()
            .pipe(
                switchMap(() => this.openUniversityModalService.openRemoveProductFromCartConfirmation(this._courseUnitId)),
                switchMap(() => this.openUniversityCartService.removeProductFromCurrentCart(this.productId)),
                take(1),
                this.appErrorHandler.defaultErrorHandler(),
            )
            .subscribe()
            .add(() => this._requestInProgress$.next(false));
    }

    private addProductToCart(): Observable<OpenUniversityCart> {
        return this._accessToken$.pipe(
            take(1),
            concatMap((accessToken: string | null) =>
                this.openUniversityCartService.addProductToCurrentCart({
                    openUniversityProductId: this.productId,
                    accessToken,
                }),
            ),
        );
    }

    private replaceProductInCart(): Observable<OpenUniversityCart> {
        // The cart already has another product from the same course unit; ask if the user wants to replace it.
        // Currently, this can't be done atomically; the old product needs to be removed and then the new one added.
        return this.openUniversityModalService.openProductReplacementConfirmation().pipe(
            switchMap(() => this.openUniversityCartService.removeCourseUnitFromCurrentCart(this._courseUnitId)),
            take(1),
            switchMap(() => this.addProductToCart()),
        );
    }

    validateNoPaymentInProgress(): Observable<void | never> {
        return this.openUniversityCartService.isPaymentInProgress().pipe(
            take(1),
            tap(isPaymentInProgress => isPaymentInProgress && this.openUniversityErrorHelper.showPaymentInProgressFailureAlert()),
            switchMap(isPaymentInProgress => (isPaymentInProgress ? EMPTY : of(null))),
        );
    }
}
